import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { NewsItem } from "@/types/news";
import { getCategoryName } from "@/utils/news";

interface HeroCarouselProps {
  items: NewsItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const count = items.length;

  const go = useCallback(
    (next: number) => {
      if (count === 0) {
        return;
      }
      setIndex(((next % count) + count) % count);
    },
    [count]
  );

  useEffect(() => {
    if (count <= 1) {
      return;
    }
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % count);
    }, 6000);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) {
    return (
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto flex h-64 w-full max-w-shell flex-col items-center justify-center px-4 text-center md:h-80 md:px-6">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
            <i className="ri-newspaper-line" />
          </span>
          <p className="mt-4 text-sm text-foreground-600">暂无焦点新闻，敬请期待</p>
        </div>
      </section>
    );
  }

  const active = items[index];

  return (
    <section
      className="relative w-full overflow-hidden border-b border-background-200"
      onTouchStart={(event) => {
        touchStartX.current = event.touches[0].clientX;
      }}
      onTouchEnd={(event) => {
        if (touchStartX.current === null) {
          return;
        }
        const delta = event.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > 50) {
          go(index + (delta < 0 ? 1 : -1));
        }
        touchStartX.current = null;
      }}
    >
      <div className="relative h-[440px] w-full md:h-[600px]">
        {items.map((item, slideIndex) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              slideIndex === index ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden={slideIndex !== index}
          >
            <img
              src={item.image}
              alt={item.title}
              title={`${item.title} - 911爆料`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        ))}

        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/75 to-background-50/20" />

        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto w-full max-w-shell px-4 pb-8 md:px-6 md:pb-12">
            <div className="max-w-3xl animate-soft-in" key={active.id}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="flex items-center gap-1.5 rounded-md bg-primary-500 px-2.5 py-1 text-xs font-semibold text-foreground-950">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-2 w-2 animate-pulse-dot rounded-full bg-foreground-950" />
                  </span>
                  焦点爆料
                </span>
                <span className="rounded-md bg-background-200/80 px-2.5 py-1 text-xs font-medium text-foreground-700">
                  {getCategoryName(active.category)}
                </span>
                {active.exclusive && (
                  <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-bold text-background-50">
                    独家
                  </span>
                )}
              </div>

              <h2 className="mt-4 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-5xl">
                {active.title}
              </h2>
              <p className="mt-3 line-clamp-2 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-base">
                {active.summary}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${active.id}`}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-600"
                >
                  阅读全文
                  <i className="ri-arrow-right-line" />
                </Link>
                <Link
                  to={`/category/${active.category}`}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100/70 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-500"
                >
                  <i className="ri-folder-line" />
                  {getCategoryName(active.category)}分类
                </Link>
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {items.map((item, dotIndex) => (
                  <button
                    key={item.id}
                    type="button"
                    aria-label={`切换到第 ${dotIndex + 1} 条焦点新闻`}
                    onClick={() => go(dotIndex)}
                    className={`h-1.5 rounded-full transition-all ${
                      dotIndex === index
                        ? "w-8 bg-primary-500"
                        : "w-4 bg-background-400 hover:bg-background-500"
                    }`}
                  />
                ))}
              </div>

              <div className="hidden items-center gap-2 md:flex">
                <button
                  type="button"
                  aria-label="上一条"
                  onClick={() => go(index - 1)}
                  className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300 bg-background-100/70 text-lg text-foreground-800 transition-colors hover:border-primary-500"
                >
                  <i className="ri-arrow-left-line" />
                </button>
                <button
                  type="button"
                  aria-label="下一条"
                  onClick={() => go(index + 1)}
                  className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300 bg-background-100/70 text-lg text-foreground-800 transition-colors hover:border-primary-500"
                >
                  <i className="ri-arrow-right-line" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}